import { Box, Typography, Button, Alert, Stack, Divider, useTheme, alpha } from "@mui/material";
import { Stop, DeleteForever } from "@mui/icons-material";
import PieChartIcon from "@mui/icons-material/PieChart";
import { useResponsive } from "../../../core/hooks/useResponsive";
import { formatInr } from "../ptFormat";

/** Headline figure — one of the two big numbers at the top of the panel. */
const Headline = ({ label, value, valueColor, note }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography
      sx={{ fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "text.secondary", lineHeight: 1.4 }}
    >
      {label}
    </Typography>
    <Typography
      fontWeight={900}
      title={typeof value === "string" ? value : undefined}
      sx={{
        letterSpacing: "-0.03em",
        lineHeight: 1.15,
        color: valueColor,
        fontSize: { xs: "1.3rem", sm: "1.5rem" },
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
    {note && (
      <Typography sx={{ fontSize: "0.65rem", color: "text.secondary", lineHeight: 1.3 }}>{note}</Typography>
    )}
  </Box>
);

/** Compact label-over-value cell — no border or icon, so six of them cost one row. */
const Stat = ({ label, value, color }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      noWrap
      sx={{ fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "text.secondary", lineHeight: 1.5 }}
    >
      {label}
    </Typography>
    <Typography noWrap title={value} sx={{ fontSize: "0.88rem", fontWeight: 800, lineHeight: 1.3, color: color || "text.primary" }}>
      {value}
    </Typography>
  </Box>
);

const CurrentSessionCard = ({ status, onClose, onDelete, busy }) => {
  const theme = useTheme();
  const { isPhone } = useResponsive();
  const primary = theme.palette.primary.main;

  if (!status) return null;

  // No PaperTradeHistory row and nothing in PaperTradeBook either - genuinely nothing to show
  // or close yet. No separate "setup" action needed; this resolves itself the moment a trade
  // is placed (getStatus will then synthesize the implicit session from that trade's date).
  if (!status.current) {
    return (
      <Box sx={{ py: 2, px: 1.5, borderRadius: 2, border: `1px dashed ${alpha(primary, 0.35)}`, textAlign: "center" }}>
        <PieChartIcon sx={{ fontSize: 28, color: alpha(primary, 0.35), mb: 0.25 }} />
        <Typography variant="body2" color="text.secondary">
          No paper trades yet — start trading to begin your first session.
        </Typography>
      </Box>
    );
  }

  const { current } = status;
  const netColor = current.netProfit >= 0 ? "success.main" : "error.main";
  const openPLColor = current.openPL >= 0 ? "success.main" : "error.main";
  const canClose = current.openPositions === 0;
  const hasOpen = current.openPositions > 0;
  const hasTrades = Number(current.amountUtilized) > 0 || hasOpen;
  const money = (n) => formatInr(n, { compact: isPhone });

  const stats = [
    { label: "Budget", value: money(current.tradeBudget) },
    // "Utilized" is the running total of every buy this session, so it can exceed the
    // budget after a few round trips - it is not current exposure. "Invested" is.
    { label: "Utilized", value: money(current.amountUtilized) },
    ...(hasOpen
      ? [
          { label: "Invested", value: money(current.investedValue) },
          { label: "Holding", value: money(current.currentHoldingValue) },
        ]
      : []),
    { label: "Profit", value: money(current.totalProfit), color: "success.main" },
    { label: "Loss", value: money(current.totalLoss), color: "error.main" },
  ];

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${alpha(primary, 0.16)}`,
          mb: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          divider={<Divider orientation="vertical" flexItem sx={{ borderColor: alpha(primary, 0.2) }} />}
          sx={{
            px: 1.5,
            py: 1.1,
            background: `linear-gradient(135deg, ${alpha(primary, 0.12)} 0%, ${alpha(
              theme.palette.secondary?.main ?? primary,
              0.08
            )} 100%)`,
          }}
        >
          <Headline label="Closed net" value={formatInr(current.netProfit)} valueColor={netColor} note="Realised" />
          {hasOpen && (
            <Headline
              label="Open P&L"
              value={formatInr(current.openPL)}
              valueColor={openPLColor}
              note={`${current.openPositions} position${current.openPositions === 1 ? "" : "s"} at market`}
            />
          )}
        </Stack>

        <Box
          sx={{
            px: 1.5,
            py: 1.1,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
            columnGap: 1.5,
            rowGap: 1,
            borderTop: `1px solid ${alpha(primary, 0.12)}`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          {stats.map((s) => (
            <Stat key={s.label} {...s} />
          ))}
        </Box>
      </Box>

      {!canClose && (
        <Alert
          severity="info"
          sx={{ mb: 1, py: 0, "& .MuiAlert-message": { py: 0.6, fontSize: "0.78rem" }, "& .MuiAlert-icon": { py: 0.6 } }}
        >
          Sell the {current.openPositions} open position{current.openPositions === 1 ? "" : "s"} to close this session,
          or delete it to discard them.
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Stop />}
          onClick={onClose}
          disabled={!canClose || busy}
          sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2 }}
        >
          Close Paper Trade
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<DeleteForever />}
          onClick={onDelete}
          disabled={!hasTrades || busy}
          sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2 }}
        >
          Delete Current Session
        </Button>
      </Stack>
    </Box>
  );
};

export default CurrentSessionCard;
